import React from 'react';
import {
  List,
  TopToolbar,
  CreateButton,
  ExportButton,
  useRedirect,
  useListContext,
} from 'react-admin';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Chip, 
  Avatar
} from '@mui/material';
import { 
  PersonAdd as LeadIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { CustomEmptyState } from '../../components/CustomEmptyState';
import { PageTitle } from '../../components/PageTitle';
import { getCustomerTypeColor } from '../../utils/customerTypeColors';

const ListActions = () => (
  <TopToolbar>
    <CreateButton />
    <ExportButton />
  </TopToolbar>
);

const StatusChip = ({ record }: { record: any }) => {
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'new':
        return { bgcolor: '#E3F2FD', color: '#1976D2' }; // Light blue - fresh, needs attention
      case 'contacted':
        return { bgcolor: '#FFF3E0', color: '#F57C00' }; // Orange - in progress, warming up
      case 'qualified':
        return { bgcolor: '#E8F5E8', color: '#388E3C' }; // Green - positive, moving forward
      case 'converted':
        return { bgcolor: '#E1F5FE', color: '#00ACC1' }; // Teal - success, completed
      case 'lost':
        return { bgcolor: '#FFEBEE', color: '#D32F2F' }; // Red - negative outcome
      case 'unqualified':
        return { bgcolor: '#F3E5F5', color: '#7B1FA2' }; // Purple - determined not suitable
      case 'nurturing':
        return { bgcolor: '#FFF8E1', color: '#FBC02D' }; // Yellow - requires patience/time
      default:
        return { bgcolor: '#F5F5F5', color: '#666666' }; // Grey - unknown/default
    }
  };

  const colorStyle = getStatusColor(record.status);

  return (
    <Chip
      label={record.status?.toUpperCase() || 'UNKNOWN'}
      size="small"
      sx={{
        bgcolor: colorStyle.bgcolor,
        color: colorStyle.color,
        fontWeight: 600,
        height: 24,
        fontSize: '0.7rem',
        '& .MuiChip-label': {
          px: 1
        }
      }}
    />
  );
};

const LeadCard = ({ record, onClick }: { record: any; onClick: () => void }) => {
  const displayName = record.displayName || 'Unnamed Lead';

  const customerTypeName = record?.customerType?.name;
  const customerTypeColor = record?.customerType?.color || getCustomerTypeColor(customerTypeName);
  
  return (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: 2,
        },
        borderLeft: `4px solid ${customerTypeColor}`,
        borderRadius: 0,
        minHeight: 'auto',
        mb: 1,
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: 'minmax(200px, 1fr) minmax(120px, 0.8fr) minmax(200px, 1.2fr) minmax(120px, 0.8fr) 1fr minmax(120px, 0.8fr)',
          },
          gap: { xs: 1, sm: 2 },
          alignItems: { xs: 'flex-start', md: 'center' },
          width: '100%'
        }}>
          {/* Column 1: Avatar and Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar 
              sx={{ 
                bgcolor: customerTypeColor,
                width: 36,
                height: 36
              }}
            >
              {record.company ? (
                <BusinessIcon sx={{ fontSize: 20, color: 'white' }} />
              ) : (
                <LeadIcon sx={{ fontSize: 20, color: 'white' }} />
              )}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography 
                variant="subtitle1" 
                component="h3" 
                sx={{ 
                  fontWeight: 600, 
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {displayName}
              </Typography>
              {record.company && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: '0.75rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {record.company}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Column 2: Source */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {record.source || '-'}
            </Typography>
          </Box>

          {/* Column 3: Contact Information */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, gridColumn: { xs: 'span 1', md: 'span 1' } }}>
            {record.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: '0.8rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {record.phone}
                </Typography>
              </Box>
            )}
            {record.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: '0.8rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {record.email}
                </Typography>
              </Box>
            )}
            {!record.phone && !record.email && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                -
              </Typography>
            )}
          </Box>

          {/* Column 4: Assigned To */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: '0.8rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {record.assignedUser?.name || record.assignedUser?.email || '-'}
            </Typography>
          </Box>

          {/* Column 5: Additional Info (spacer) */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}></Box>

          {/* Column 6: Status and Tags */}
          <Box sx={{
            display: 'flex',
            gap: 0.5,
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            flexWrap: 'wrap',
            gridColumn: { xs: 'span 1', sm: 'span 1' }
          }}>
            <StatusChip record={record} />
            {customerTypeName && (
              <Chip 
                label={customerTypeName}
                size="small"
                sx={{ 
                  bgcolor: customerTypeColor,
                  color: 'white',
                  fontWeight: 500,
                  height: 24,
                  fontSize: '0.7rem'
                }}
              />
            )}
            {record.qualified && (
              <Chip 
                label="Qualified"
                size="small"
                color="success"
                sx={{ 
                  fontWeight: 500,
                  height: 24,
                  fontSize: '0.7rem'
                }}
              />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const LeadListHeader = () => (
  <Box sx={{
    display: {
      xs: 'none',
      md: 'grid'
    },
    gridTemplateColumns: 'minmax(200px, 1fr) minmax(120px, 0.8fr) minmax(200px, 1.2fr) minmax(120px, 0.8fr) 1fr minmax(120px, 0.8fr)',
    gap: 2,
    alignItems: 'center',
    px: 2,
    py: 1.5,
    backgroundColor: 'grey.50',
    borderRadius: 0,
    mb: 2,
    border: '1px solid',
    borderColor: 'grey.200'
  }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      Lead Name
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      Source
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      Contact Information
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      Assigned To
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      {/* Additional Info */}
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'right' }}>
      Status
    </Typography>
  </Box>
);

const LeadCardList = () => {
  const { data, isLoading } = useListContext();
  const redirect = useRedirect();

  if (isLoading) {
    return <Box>Loading...</Box>;
  }

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <Box>
      <LeadListHeader />
      {data.map((record: any) => (
        <LeadCard 
          key={record.id}
          record={record} 
          onClick={() => redirect(`/leads/${record.id}/show`)}
        />
      ))}
    </Box>
  );
};

export const LeadList = () => {
  const redirect = useRedirect();

  const handleCreateLead = () => {
    redirect('/leads/create');
  };

  return (
    <Box>
      <PageTitle
        title="Leads"
        titleKey="pages.leads"
        subtitle="Potential customers and prospects"
        subtitleKey="pages.leads.subtitle"
        icon={<LeadIcon />}
      />
      <List
        actions={<ListActions />}
        perPage={25}
        sort={{ field: 'createdAt', order: 'DESC' }}
        empty={<CustomEmptyState resource="leads" onAction={handleCreateLead} />}
        sx={{ 
          '& .RaList-main': { mt: 0 },
          '& .RaList-content': { p: 2 }
        }}
      >
        <LeadCardList />
      </List>
    </Box>
  );
};
